import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { TaxReportDownloadDropdown } from '@/modules/issuer-dashboard/components/tax-reports/TaxReportDownloadDropdown.tsx';
import { useGenerateTaxReportDownload } from '@/modules/issuer-dashboard/hooks/useGenerateTaxReportDownload.ts';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import {
  getTaxReportYearOptions,
} from '@/modules/issuer-dashboard/helpers/taxReportYears.ts';
import {
  useHolderTaxReportYearsQuery,
  useUpsertHolderTaxReportByYearDataMutation,
} from '@/modules/issuer-dashboard/queries/tax-reports.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { Button, DisclosureListItem, EmptyListState, ModalContentWrapper, Select, Spinner } from 'ui';

interface TaxReportsTabProps {
  entityId: number;
  registerId: number;
  holderDisplayName?: string;
}

const TaxReportsTab = ({ entityId, registerId, holderDisplayName }: TaxReportsTabProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data, isLoading } = useHolderTaxReportYearsQuery(
    { entity_id: entityId, register_id: registerId },
    !entityId || !registerId,
  );

  const items = useMemo(() => data?.data || [], [data?.data]);
  const usedYears = useMemo(() => new Set(items.map(item => item.year)), [items]);
  const yearOptions = useMemo(() => getTaxReportYearOptions(11), []);
  const availableYearOptions = useMemo(
    () => yearOptions.filter(option => !usedYears.has(option.value)),
    [usedYears, yearOptions],
  );
  const [createYear, setCreateYear] = useState<number | null>(availableYearOptions[0]?.value ?? null);

  const { mutateAsync: upsertHolderTaxReport, isLoading: isCreating } =
    useUpsertHolderTaxReportByYearDataMutation();
  const { generateTaxReport, isDownloading } = useGenerateTaxReportDownload();

  const handleGoToEdit = (year: number) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set('year', String(year));
    const currentSearch = params.toString();
    const from = currentSearch ? `${pathname}?${currentSearch}` : pathname;

    navigate(
      {
        pathname: issuerDashboardPaths.dynamic.taxReportHolderEdit(
          String(registerId),
          String(entityId),
        ),
        search: nextParams.toString(),
      },
      {
        state: { from, holderDisplayName },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="w-full flex justify-end">
        <Button
          className="w-max"
          onClick={() => {
            setCreateYear(availableYearOptions[0]?.value ?? null);
            setIsCreateModalOpen(true);
          }}
        >
          Create tax report
        </Button>
      </div>

      {isLoading && (
        <div className="h-full w-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      <EmptyListState
        title="No tax reports yet"
        description="No saved tax reports were found for this holder."
        isLoading={isLoading}
        isSearchUsed={false}
        itemsCount={items.length}
      />

      {!isLoading && !!items.length && (
        <div className="space-y-3">
          {items.map(item => (
            <DisclosureListItem
              key={item.id}
              onClick={() => void handleGoToEdit(item.year)}
              leftMainString={item.year}
              leftSubduedContentSet={[
                dateToPrint(item.created_at, DateVariant.Table) || '-',
                item.register_name || '-',
              ]}
              rightContent={
                <div
                  className="flex items-center gap-2 pr-4 shrink-0"
                  onClick={e => e.stopPropagation()}
                >
                  <Button
                    variant="plain"
                    className="w-[158px] py-0 min-h-6 px-0"
                    onClick={() => void handleGoToEdit(item.year)}
                  >
                    Edit tax report
                  </Button>
                  <TaxReportDownloadDropdown
                    disabled={isDownloading}
                    onDownload={format =>
                      void generateTaxReport({
                        registerId,
                        entityId,
                        financialYear: item.year,
                        filename: `${item.register_name || 'Register'} Tax Report ${item.year}`,
                        format,
                      })
                    }
                  />
                </div>
              }
            />
          ))}
        </div>
      )}

      <ModalContentWrapper
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create tax report"
      >
        <div className="w-full flex flex-col gap-6">
          <Select<number>
            options={availableYearOptions}
            label="Year"
            value={createYear ?? undefined}
            onChange={year => setCreateYear(year ?? null)}
          />
          <Button
            loading={isCreating}
            disabled={isCreating || !createYear || !availableYearOptions.length}
            onClick={async () => {
              if (!createYear) return;
              await upsertHolderTaxReport({
                year: createYear,
                entity_id: entityId,
                register_id: registerId,
                tax_report_json: {},
              });
              setIsCreateModalOpen(false);
            }}
          >
            Create
          </Button>
          {!availableYearOptions.length && (
            <div className="text-sm text-gray-500">No available years to create.</div>
          )}
        </div>
      </ModalContentWrapper>
    </div>
  );
};

export default TaxReportsTab;
